import { useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { badgedTitle } from "@/lib/unread-tab-badge";

const FAVICON_HREF = "/favicon.svg";
const ICON_SIZE = 64;
/** Unread dot: red with a white ring so it reads on the blue mark. */
const DOT_FILL = "#d92d20";
const DOT_RING = "#ffffff";

function faviconLink(): HTMLLinkElement | null {
  return document.querySelector<HTMLLinkElement>('link[rel="icon"]');
}

/** Draws the favicon with an unread dot in the top-right corner. */
function drawBadgedFavicon(onReady: (href: string) => void): () => void {
  let cancelled = false;
  const img = new Image();
  img.onload = () => {
    if (cancelled) return;
    const canvas = document.createElement("canvas");
    canvas.width = ICON_SIZE;
    canvas.height = ICON_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, ICON_SIZE, ICON_SIZE);
    const r = ICON_SIZE * 0.22;
    const cx = ICON_SIZE - r - 2;
    const cy = r + 2;
    ctx.beginPath();
    ctx.arc(cx, cy, r + 4, 0, Math.PI * 2);
    ctx.fillStyle = DOT_RING;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = DOT_FILL;
    ctx.fill();
    onReady(canvas.toDataURL("image/png"));
  };
  img.src = FAVICON_HREF;
  return () => {
    cancelled = true;
  };
}

/**
 * Mirrors the unread notification count on the browser tab: "(3) Iamu" in
 * the title and a dot on the favicon. Shares the bell's 30s unread query, so
 * mount it once per app shell.
 */
export function useUnreadTabBadge(): void {
  const { data } = trpc.notification.unreadCount.useQuery(undefined, {
    refetchInterval: 30_000,
  });
  const unreadCount = data?.count ?? 0;

  useEffect(() => {
    document.title = badgedTitle(document.title, unreadCount);
    const link = faviconLink();
    if (!link) return;
    if (unreadCount <= 0) {
      link.href = FAVICON_HREF;
      return;
    }
    return drawBadgedFavicon((href) => {
      link.href = href;
    });
  }, [unreadCount]);

  useEffect(
    () => () => {
      document.title = badgedTitle(document.title, 0);
      const link = faviconLink();
      if (link) link.href = FAVICON_HREF;
    },
    [],
  );
}
